import { initFirebase } from '../firebase'
import { collection, getDocs, query, orderBy, limit, doc, getDoc, where } from 'firebase/firestore'

const { db } = initFirebase()

export class FirebaseChatExportService {
  async getAdaptyProfile(userId) {
    try {
      const response = await fetch('https://api.adapty.io/api/v2/server-side-api/profile/', {
        method: 'GET',
        headers: {
          'adapty-customer-user-id': userId,
          'Content-Type': 'application/json',
          'Authorization': 'Api-Key secret_live_PRtNsgF1.hz6WooeMJOtR1B6VPhwrIZKxIBK2XMJL'
        }
      })

      if (!response.ok) {
        return { ltv: null, cancellationReason: null, subscriptionStatus: null, productId: null, planType: null, activatedAt: null, expiresAt: null, renewalCancelled: false, store: null, environment: null }
      }

      const data = await response.json()
      const profileData = data.data || {}

      const profileId = profileData.profile_id || null
      const ltv = profileData.total_revenue_usd != null ? profileData.total_revenue_usd : null

      // access_levels and subscriptions are arrays in Adapty API v2
      const accessLevels = Array.isArray(profileData.access_levels) ? profileData.access_levels : []
      const subs = Array.isArray(profileData.subscriptions) ? profileData.subscriptions : []

      const accessLevel = accessLevels[0] || null
      const sub = subs[0] || null

      // Actual field names from Adapty API v2:
      //   store_product_id, purchased_at, expires_at, renewal_cancelled_at, cancellation_reason, store, environment
      const productId = sub?.store_product_id || accessLevel?.store_product_id || null
      const activatedAt = sub?.purchased_at || accessLevel?.purchased_at || null
      const expiresAt = sub?.expires_at || accessLevel?.expires_at || null
      const store = sub?.store || accessLevel?.store || null
      const environment = sub?.environment || null
      const cancellationReason = sub?.cancellation_reason || accessLevel?.cancellation_reason || null
      const renewalCancelled = !!(sub?.renewal_cancelled_at || accessLevel?.renewal_cancelled_at)

      // No is_active field — determine from expires_at
      let subscriptionStatus = null
      if (accessLevel || sub) {
        const isActive = expiresAt ? new Date(expiresAt) > new Date() : false
        subscriptionStatus = isActive ? (renewalCancelled ? 'active (cancel pending)' : 'active') : 'expired'
      }

      let planType = null
      if (productId) {
        const pid = productId.toLowerCase()
        if (pid.includes('weekly')) planType = 'Weekly'
        else if (pid.includes('monthly')) planType = 'Monthly'
        else if (pid.includes('yearly') || pid.includes('annual')) planType = 'Yearly'
        else planType = 'Unknown'
      }

      return { profileId, ltv, cancellationReason, subscriptionStatus, productId, planType, activatedAt, expiresAt, renewalCancelled, store, environment }
    } catch (error) {
      console.error(`Error fetching Adapty profile for ${userId}:`, error)
      return { profileId: null, ltv: null, cancellationReason: null, subscriptionStatus: null, productId: null, planType: null, activatedAt: null, expiresAt: null, renewalCancelled: false, store: null, environment: null }
    }
  }

  // Backward compatible alias
  async getAdaptyLTV(userId) {
    const profile = await this.getAdaptyProfile(userId)
    return { ltv: profile.ltv, cancellationReason: profile.cancellationReason }
  }

  calculateAge(year, month, day) {
    if (!year || !month || !day) return null
    
    const birthDate = new Date(year, month - 1, day)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    
    return age
  }

  async getRecentUsersWithQuestions(userLimit = 500, progressCallback = null) {
    try {
      const chatsRef = collection(db, 'chats')
      const chatsQuery = query(chatsRef, orderBy('createdAt', 'desc'), limit(userLimit))
      const chatsSnapshot = await getDocs(chatsQuery)

      console.log(`✅ Chats fetched: ${chatsSnapshot.docs.length}`)

      const usersData = []
      const totalChats = chatsSnapshot.docs.length
      let processedChats = 0
      let totalProcessingTime = 0

      for (const chatDoc of chatsSnapshot.docs) {
        const iterationStartTime = Date.now()
        
        const chatData = chatDoc.data()
        const userId = chatData.userId
        const createdAt = chatData.createdAt

        if (!userId) {
          processedChats++
          continue
        }

        const messages = chatData.messages || []
        const userMessages = messages
          .filter(msg => msg.role === 'user' && msg.originalContent)
          .map(msg => msg.originalContent)

        if (userMessages.length === 0) {
          processedChats++
          continue
        }

        const userDocRef = doc(db, 'users', userId)
        const userSnap = await getDoc(userDocRef)

        if (!userSnap.exists()) {
          processedChats++
          continue
        }

        const userData = userSnap.data()
        const properties = userData.properties || {}
        const astrology = userData.astrology || {}

        const gender = properties.gender || '-'
        const age = this.calculateAge(properties.year, properties.month, properties.date) || '-'
        const name = properties.name || '-'
        const idfv = properties.idfv || '-'
        const whyUserNotPurchase = properties.whyUserNotPurchase || '-'
        
        const sunSign = astrology.planets?.find(p => p.planet === 'Sun')?.zodiac || '-'
        
        const formattedCreatedAt = createdAt 
          ? (createdAt.seconds 
              ? new Date(createdAt.seconds * 1000).toLocaleString('tr-TR', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })
              : new Date(createdAt).toLocaleString('tr-TR', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                }))
          : '-'

        usersData.push({
          userId,
          name,
          idfv,
          gender,
          age,
          sign: sunSign,
          whyUserNotPurchase,
          createdAt: formattedCreatedAt,
          questions: userMessages
        })

        processedChats++
        
        const iterationTime = Date.now() - iterationStartTime
        totalProcessingTime += iterationTime
        const averageTimePerChat = totalProcessingTime / processedChats
        const remainingChats = totalChats - processedChats
        const estimatedTimeRemaining = (averageTimePerChat * remainingChats) / 1000

        if (progressCallback) {
          progressCallback({
            current: processedChats,
            total: totalChats,
            estimatedTimeRemaining,
            processingTime: iterationTime
          })
        }
      }

      return usersData
    } catch (error) {
      console.error('Error fetching recent users with questions:', error)
      throw error
    }
  }

  async getUserInfo(userId) {
    try {
      const userRef = doc(db, 'users', userId)
      const userSnap = await getDoc(userRef)
      
      if (!userSnap.exists()) {
        return { gender: null, age: null }
      }
      
      const userData = userSnap.data()
      const properties = userData.properties || {}
      
      const gender = properties.gender || null
      const age = this.calculateAge(properties.year, properties.month, properties.date)
      
      return { gender, age }
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error)
      return { gender: null, age: null }
    }
  }

  async getMessagesInDateRange(startDate, endDate) {
    try {
      const chatsRef = collection(db, 'chats')
      const chatsQuery = query(chatsRef, orderBy('createdAt', 'desc'), limit(500))
      const chatsSnap = await getDocs(chatsQuery)
      
      console.log(`✅ Chats fetched: ${chatsSnap.docs.length}`)
      
      const allMessages = []
      const userCache = new Map()
      let chatIndex = 0
      const totalChats = chatsSnap.docs.length
      
      for (const chatDoc of chatsSnap.docs) {
        chatIndex++
        const userId = chatDoc.data().userId || chatDoc.id
        
        const chatData = chatDoc.data()
        const messages = chatData.messages || []
        
        for (const msg of messages) {
          if (msg.role === 'user') {
            const msgDate = msg.createdAt?.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt)
            
            if (msgDate >= startDate && msgDate <= endDate) {
              if (!userCache.has(userId)) {
                console.log(`📥 Fetching user data: ${chatIndex}/${totalChats}`)
                const userInfo = await this.getUserInfo(userId)
                userCache.set(userId, userInfo)
              }
              
              const userInfo = userCache.get(userId)
              
              allMessages.push({
                userId,
                content: msg.originalContent || msg.content || '',
                createdAt: msg.createdAt,
                gender: userInfo.gender,
                age: userInfo.age
              })
            }
          }
        }
      }
      
      return allMessages
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  }

  async getUserDetailedInfo(userId) {
    try {
      const userRef = doc(db, 'users', userId)
      const userSnap = await getDoc(userRef)
      
      if (!userSnap.exists()) {
        return null
      }
      
      const userData = userSnap.data()
      const properties = userData.properties || {}
      const astrology = userData.astrology || {}
      const attribution = userData.attribution || {}
      
      const planets = astrology.planets || []
      const sunSign = planets.find(p => p.planet === 'Sun')?.zodiac || null
      const moonSign = planets.find(p => p.planet === 'Moon')?.zodiac || null
      const ascendantSign = planets.find(p => p.planet === 'Ascendant')?.zodiac || null
      
      const age = this.calculateAge(properties.year, properties.month, properties.date)
      
      const adaptyData = await this.getAdaptyLTV(userId)
      
      return {
        gender: properties.gender || null,
        age,
        sunSign,
        moonSign,
        ascendantSign,
        whyUserCame: properties.whyUserCame || null,
        whyUserNotPurchase: properties.whyUserNotPurchase || null,
        keyword: attribution.details?.keywordText || null,
        campaignName: attribution.details?.campaignName || null,
        ltv: adaptyData.ltv,
        cancellationReason: adaptyData.cancellationReason
      }
    } catch (error) {
      console.error(`Error fetching detailed user info ${userId}:`, error)
      return null
    }
  }

  async getUsersInDateRange(startDate, endDate, progressCallback = null) {
    try {
      const usersRef = collection(db, 'users')
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'))
      const usersSnap = await getDocs(usersQuery)

      console.log(`✅ All users fetched: ${usersSnap.docs.length}`)

      // Filter users by createdAt date range
      const usersInRange = usersSnap.docs.filter(userDoc => {
        const userData = userDoc.data()
        const createdAt = userData.createdAt
        if (!createdAt) return false

        const userDate = createdAt.seconds
          ? new Date(createdAt.seconds * 1000)
          : new Date(createdAt)

        return userDate >= startDate && userDate <= endDate
      })

      console.log(`✅ Users in date range: ${usersInRange.length}`)

      // Fetch all chats at once to build message count map
      const chatsRef = collection(db, 'chats')
      const chatsQuery = query(chatsRef)
      const chatsSnap = await getDocs(chatsQuery)

      console.log(`✅ All chats fetched: ${chatsSnap.docs.length}`)

      // Build user message count map
      const userMessagesMap = new Map()
      for (const chatDoc of chatsSnap.docs) {
        const userId = chatDoc.data().userId
        if (!userId) continue

        const messages = chatDoc.data().messages || []
        const userMsgCount = messages.filter(m => m.role === 'user').length

        if (!userMessagesMap.has(userId)) {
          userMessagesMap.set(userId, 0)
        }
        userMessagesMap.set(userId, userMessagesMap.get(userId) + userMsgCount)
      }

      // Fetch Cancel Feedback from feedback collection
      const feedbackRef = collection(db, 'feedback')
      const feedbackQuery = query(feedbackRef, where('about', '==', 'Cancel Feedback'))
      const feedbackSnap = await getDocs(feedbackQuery)

      console.log(`✅ Cancel feedbacks fetched: ${feedbackSnap.docs.length}`)

      // Build user cancel feedback map (user can have multiple feedbacks, join them)
      const userCancelFeedbackMap = new Map()
      for (const feedbackDoc of feedbackSnap.docs) {
        const feedbackData = feedbackDoc.data()
        const userId = feedbackData.userId
        if (!userId) continue

        const feedback = feedbackData.feedback || ''
        if (feedback) {
          if (!userCancelFeedbackMap.has(userId)) {
            userCancelFeedbackMap.set(userId, [])
          }
          userCancelFeedbackMap.get(userId).push(feedback)
        }
      }

      const totalUsersToProcess = usersInRange.length
      const usersData = []
      let userIndex = 0
      const startTime = Date.now()

      if (progressCallback) {
        progressCallback({
          current: 0,
          total: totalUsersToProcess,
          estimatedTimeRemaining: null,
          processingTime: null
        })
      }

      for (const userDoc of usersInRange) {
        userIndex++
        const userStartTime = Date.now()
        const userId = userDoc.id
        const userData = userDoc.data()

        console.log(`📥 Processing user: ${userIndex}/${totalUsersToProcess}`)

        const properties = userData.properties || {}
        const astrology = userData.astrology || {}
        const attribution = userData.attribution || {}

        const planets = astrology.planets || []
        const sunSign = planets.find(p => p.planet === 'Sun')?.zodiac || null
        const moonSign = planets.find(p => p.planet === 'Moon')?.zodiac || null
        const ascendantSign = planets.find(p => p.planet === 'Ascendant')?.zodiac || null

        const age = this.calculateAge(properties.year, properties.month, properties.date)
        const messageCount = userMessagesMap.get(userId) || 0
        const cancelFeedbacks = userCancelFeedbackMap.get(userId) || []
        const cancelFeedback = cancelFeedbacks.length > 0 ? cancelFeedbacks.join(' | ') : null

        // Fetch Adapty Profile
        const adaptyData = await this.getAdaptyProfile(userId)

        usersData.push({
          userId,
          gender: properties.gender || null,
          age,
          sunSign,
          moonSign,
          ascendantSign,
          whyUserCame: properties.whyUserCame || null,
          whyUserNotPurchase: properties.whyUserNotPurchase || null,
          keyword: attribution.details?.keywordText || null,
          campaignName: attribution.details?.campaignName || null,
          messages: messageCount,
          ltv: adaptyData.ltv,
          subscriptionStatus: adaptyData.subscriptionStatus,
          planType: adaptyData.planType,
          productId: adaptyData.productId,
          activatedAt: adaptyData.activatedAt,
          expiresAt: adaptyData.expiresAt,
          store: adaptyData.store,
          cancellationReason: adaptyData.cancellationReason,
          cancelFeedback
        })

        const userEndTime = Date.now()
        const userProcessingTime = userEndTime - userStartTime

        const avgTimePerUser = (Date.now() - startTime) / userIndex
        const remainingUsers = totalUsersToProcess - userIndex
        const estimatedTimeRemaining = Math.ceil((avgTimePerUser * remainingUsers) / 1000)

        if (progressCallback) {
          progressCallback({
            current: userIndex,
            total: totalUsersToProcess,
            estimatedTimeRemaining,
            processingTime: userProcessingTime
          })
        }
      }

      return usersData
    } catch (error) {
      console.error('Error fetching users in date range:', error)
      throw error
    }
  }

  async getUsersWithMinMessages(chatLimit = null, minMessageCount = 5, progressCallback = null) {
    try {
      const chatsRef = collection(db, 'chats')
      const chatsQuery = chatLimit 
        ? query(chatsRef, orderBy('createdAt', 'desc'), limit(chatLimit))
        : query(chatsRef, orderBy('createdAt', 'desc'))
      const chatsSnap = await getDocs(chatsQuery)
      
      console.log(`✅ Chats fetched: ${chatsSnap.docs.length}`)
      
      const userMessagesMap = new Map()
      
      for (const chatDoc of chatsSnap.docs) {
        const userId = chatDoc.data().userId || chatDoc.id
        const chatData = chatDoc.data()
        const messages = chatData.messages || []
        
        if (!userMessagesMap.has(userId)) {
          userMessagesMap.set(userId, [])
        }
        
        for (const msg of messages) {
          if (msg.role === 'user') {
            const content = msg.originalContent || msg.content || ''
            userMessagesMap.get(userId).push(content)
          }
        }
      }
      
      const usersWithMinMessagesIds = Array.from(userMessagesMap.entries())
        .filter(([, messages]) => messages.length >= minMessageCount)
      
      const totalUsersToProcess = usersWithMinMessagesIds.length
      
      if (progressCallback) {
        progressCallback({
          current: 0,
          total: totalUsersToProcess,
          estimatedTimeRemaining: null,
          processingTime: null
        })
      }
      
      const usersWithMinMessages = []
      let userIndex = 0
      const startTime = Date.now()
      
      for (const [userId, messages] of usersWithMinMessagesIds) {
        userIndex++
        const userStartTime = Date.now()
        
        console.log(`📥 Fetching detailed user data: ${userIndex}/${totalUsersToProcess}`)
        
        const userInfo = await this.getUserDetailedInfo(userId)
        
        if (userInfo) {
          usersWithMinMessages.push({
            userId,
            ...userInfo,
            messages: messages.join(', '),
            messageCount: messages.length
          })
        }
        
        const userEndTime = Date.now()
        const userProcessingTime = userEndTime - userStartTime
        
        const avgTimePerUser = (Date.now() - startTime) / userIndex
        const remainingUsers = totalUsersToProcess - userIndex
        const estimatedTimeRemaining = Math.ceil((avgTimePerUser * remainingUsers) / 1000)
        
        if (progressCallback) {
          progressCallback({
            current: userIndex,
            total: totalUsersToProcess,
            estimatedTimeRemaining,
            processingTime: userProcessingTime
          })
        }
      }
      
      return usersWithMinMessages
    } catch (error) {
      console.error('Error fetching users with min messages:', error)
      throw error
    }
  }
}

